import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import {
  FileSpreadsheet, CheckCircle, AlertCircle, Trash2, Search,
  ChevronDown, ChevronRight, Edit2, Copy, ArrowLeft, Loader2, Check, ChevronsUpDown, Info, History, User, Calendar, Zap, ClipboardList
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { useMMDLocations } from '../../hooks/useMMDLocations';
import BulkSimilarityModal from '../../components/admin/BulkSimilarityModal';
import StagingActionsHelpPopover from '../../components/admin/StagingActionsHelpPopover';
import { StagingTrashModal } from '../../components/admin/BulkImportAuditModals';
import MiddleTruncate from '../../components/common/MiddleTruncate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const grades = ['MEO Class 1', 'MEO Class 2', 'MEO Class 4', 'ETO', 'Master', 'Chief Mate', '2MFG'];
const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                'July', 'August', 'September', 'October', 'November', 'December'];
const currentYear = new Date().getFullYear();
const years = Array.from({length: 8}, (_, i) => (currentYear - 5 + i).toString());

// Nautical grades - surveyors can come from any MMD
const NAUTICAL_GRADES = ['Master', 'Chief Mate', '2MFG'];
const isNauticalGrade = (grade) => NAUTICAL_GRADES.includes(grade);

const BulkImportReviewPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stagingData, setStagingData] = useState({ total_count: 0, file_groups: [] });
  // Trash modal — mounted at the bottom of the page. Audit-log lives on
  // its own dedicated page now (/admin/bulk-import-audit).
  const [trashOpen, setTrashOpen] = useState(false);
  
  // Fetch MMD locations from API
  const { locations: mmdLocations } = useMMDLocations();
  const [allFunctions, setAllFunctions] = useState([]);
  const [surveyors, setSurveyors] = useState([]);
  
  // Filters
  const [filterGrade, setFilterGrade] = useState('');
  const [filterMMD, setFilterMMD] = useState('');
  const [filterFunction, setFilterFunction] = useState('');
  const [functionComboOpen, setFunctionComboOpen] = useState(false);
  
  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  
  // Selected question for details panel
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [similarityData, setSimilarityData] = useState(null);
  const [loadingSimilarity, setLoadingSimilarity] = useState(false);
  // Cache fetched surveyor lists per "<grade>::<mmd>" so repeated question clicks
  // don't re-hit /api/surveyors. Also tracks the latest abort controller for the
  // similarity check, so rapid clicks cancel stale requests instead of racing.
  const surveyorCacheRef = React.useRef(new Map());
  const similarityAbortRef = React.useRef(null);

  // Bulk similarity modal state
  const [bulkSimOpen, setBulkSimOpen] = useState(false);
  const [bulkSimBatchId, setBulkSimBatchId] = useState(null);
  const [bulkSimScope, setBulkSimScope] = useState(null);
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Surveyor combobox states
  const [internalSurveyorComboOpen, setInternalSurveyorComboOpen] = useState(false);
  const [externalSurveyorComboOpen, setExternalSurveyorComboOpen] = useState(false);
  const [bulkSurveyorComboOpen, setBulkSurveyorComboOpen] = useState(false);
  
  // Bulk Edit dialog
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({
    updateSurveyor: false,
    updateMonthYear: false,
    updateDecider: false,
    asked_by_internal: false,
    surveyor_id: '',
    exam_month: '',
    exam_year: '',
    is_decider: false
  });
  const [bulkSaving, setBulkSaving] = useState(false);
  
  // Merge dialog state
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeTargetQuestion, setMergeTargetQuestion] = useState(null);
  const [mergeOptions, setMergeOptions] = useState({
    updateAnswer: false
  });
  const [isMerging, setIsMerging] = useState(false);
  
  // History modal state
  const [historyModal, setHistoryModal] = useState({ open: false, question: null });

  const fetchStagingData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/bulk-import/staging`;
      const params = new URLSearchParams();
      if (filterGrade) params.append('grade', filterGrade);
      if (filterMMD) params.append('mmd_location', filterMMD);
      if (filterFunction) params.append('function_id', filterFunction);
      
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setStagingData(response.data);
      
      // Auto-expand all groups if there are few
      if (response.data.file_groups.length <= 3) {
        setExpandedGroups(new Set(response.data.file_groups.map(g => g.import_batch_id)));
      }
    } catch (error) {
      console.error('Failed to fetch staging data:', error);
      toast.error('Failed to load staging questions');
    } finally {
      setLoading(false);
    }
  }, [filterGrade, filterMMD, filterFunction]);

  useEffect(() => {
    fetchStagingData();
    fetchFunctions();
  }, [fetchStagingData]);

  // Fetch surveyors when selected question changes (based on question's grade/mmd)
  useEffect(() => {
    if (selectedQuestion) {
      fetchSurveyorsForQuestion(selectedQuestion.grade, selectedQuestion.mmd_location);
    }
  }, [selectedQuestion?.id]);

  const fetchFunctions = async () => {
    try {
      const response = await axios.get(`${API}/functions`);
      // Filter to oral functions only - this module is for oral questions
      const oralFunctions = response.data.filter(f => f.exam_type !== 'written');
      setAllFunctions(oralFunctions);
    } catch (error) {
      console.error('Failed to fetch functions:', error);
    }
  };
  
  // Filter functions by selected grade (or show all if no grade selected)
  const filteredFunctions = useMemo(() => {
    if (!filterGrade) {
      return allFunctions;
    }
    return allFunctions.filter(f => f.grade === filterGrade);
  }, [allFunctions, filterGrade]);
  
  // Reset function filter when grade changes and current function doesn't match
  useEffect(() => {
    if (filterFunction && filterGrade) {
      const funcStillValid = filteredFunctions.some(f => f.id === filterFunction);
      if (!funcStillValid) {
        setFilterFunction('');
      }
    }
  }, [filterGrade, filterFunction, filteredFunctions]);

  const fetchSurveyorsForQuestion = async (grade, mmdLocation) => {
    // Session-level cache: same (grade, mmd) was just resolved → reuse it.
    // Eliminates a redundant /api/surveyors round-trip on every question click.
    const cacheKey = `${grade || ''}::${mmdLocation || ''}`;
    const cached = surveyorCacheRef.current.get(cacheKey);
    if (cached) {
      setSurveyors(cached);
      return;
    }
    try {
      const response = await axios.get(`${API}/surveyors`);
      const isNautical = isNauticalGrade(grade);
      const filtered = response.data.filter(s => {
        const hasGrade = s.grades?.includes(grade);
        if (isNautical) {
          return hasGrade;
        }
        const mmdNormalized = mmdLocation?.endsWith(' MMD') ? mmdLocation : `${mmdLocation} MMD`;
        const hasMMD = s.mmd_locations?.includes(mmdLocation) || s.mmd_locations?.includes(mmdNormalized);
        return hasGrade && hasMMD;
      });
      surveyorCacheRef.current.set(cacheKey, filtered);
      setSurveyors(filtered);
    } catch (error) {
      console.error('Failed to fetch surveyors:', error);
    }
  };

  // Check if question has asked_by_history
  const hasAskedByHistory = (question) => {
    return question.asked_by_history && question.asked_by_history.length > 0;
  };

  // Format exam month for display
  const formatExamMonth = (examMonth) => {
    if (!examMonth || examMonth.trim() === '') return null;
    const trimmed = examMonth.trim();
    
    // Try YYYY-MM format
    if (/^\d{4}-\d{2}$/.test(trimmed)) {
      const date = new Date(trimmed + '-01');
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      }
    }
    
    // Try "Month YYYY" format
    const monthYearMatch = trimmed.match(/^(\w+)\s+(\d{4})$/);
    if (monthYearMatch) {
      const monthName = monthYearMatch[1];
      const year = monthYearMatch[2];
      const monthIndex = months.findIndex(m => m.toLowerCase() === monthName.toLowerCase());
      if (monthIndex !== -1) {
        const date = new Date(parseInt(year), monthIndex, 1);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      }
    }
    
    return trimmed;
  };

  // Get grouped history for display (group by surveyor + exam_month)
  const getGroupedHistory = (question) => {
    const history = question.asked_by_history || [];
    if (history.length === 0) return [];
    
    // Normalize the surveyor identifier so legacy "unknown" sentinels and
    // the proper "unknown-{type}-active" IDs are treated as the SAME group.
    const SENTINEL_IDS = new Set(['unknown', 'unknown-internal-active', 'unknown-external-active']);
    const normSurveyorKey = (entry) => {
      const sid = entry.surveyor_id;
      if (!sid || SENTINEL_IDS.has(sid)) return `_unknown_${entry.surveyor_type || 'external'}`;
      return sid;
    };
    const normMonthKey = (em) => {
      if (!em || !String(em).trim()) return '_undated';
      return String(em).trim().toLowerCase().replace(/\s+/g, ' ');
    };

    // Group entries by normalized (surveyor, exam_month)
    const grouped = {};
    history.forEach(entry => {
      const surveyor = surveyors.find(s => s.id === entry.surveyor_id);
      const key = `${normSurveyorKey(entry)}|${normMonthKey(entry.exam_month)}`;

      // Friendly fallback for legacy rows where surveyor_id was saved as the
      // literal string "unknown": show "Unknown Internal"/"Unknown External"
      // based on surveyor_type instead of the raw id.
      const typeLabel = entry.surveyor_type === 'internal' ? 'Internal' : 'External';
      const fallbackName = `Unknown ${typeLabel}`;
      const displayName = surveyor?.name
        || entry.surveyor_name
        || ((!entry.surveyor_id || entry.surveyor_id === 'unknown') ? fallbackName : (entry.surveyor_id || fallbackName));

      if (!grouped[key]) {
        // For undated entries, derive a sensible display date.
        let displayMonth = entry.exam_month;
        if (!displayMonth || !String(displayMonth).trim()) {
          const ts = entry.added_at ? new Date(entry.added_at) : new Date();
          if (!isNaN(ts.getTime())) {
            displayMonth = ts.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
          }
        }
        grouped[key] = {
          surveyor_name: displayName,
          surveyor_type: typeLabel,
          exam_month: displayMonth,
          mmd_location: entry.mmd_location,
          count: 0
        };
      }
      grouped[key].count += entry.asked || 1;
    });
    
    // Convert to array and sort by exam_month (newest first)
    return Object.values(grouped).sort((a, b) => {
      if (!a.exam_month) return 1;
      if (!b.exam_month) return -1;
      return b.exam_month.localeCompare(a.exam_month);
    });
  };

  const handleSelectQuestion = async (question) => {
    setSelectedQuestion(question);
    setSimilarityData(null);

    // Cancel any in-flight similarity request for a previous question so rapid
    // clicks don't race (which would show stale similar-questions data).
    if (similarityAbortRef.current) {
      similarityAbortRef.current.abort();
    }
    const controller = new AbortController();
    similarityAbortRef.current = controller;

    setLoadingSimilarity(true);
    try {
      const response = await axios.post(
        `${API}/admin/bulk-import/check-similarity/${question.id}`,
        null,
        { signal: controller.signal }
      );
      // Only commit if this is still the latest request
      if (similarityAbortRef.current === controller) {
        setSimilarityData(response.data);
      }
    } catch (error) {
      if (axios.isCancel?.(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        return; // superseded by a newer click — silently ignore
      }
      console.error('Failed to check similarity:', error);
    } finally {
      if (similarityAbortRef.current === controller) {
        setLoadingSimilarity(false);
      }
    }
  };

  const toggleGroup = (filename) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(filename)) {
      newExpanded.delete(filename);
    } else {
      newExpanded.add(filename);
    }
    setExpandedGroups(newExpanded);
  };

  const toggleSelectQuestion = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAllInGroup = (group) => {
    const newSelected = new Set(selectedIds);
    group.questions.forEach(q => newSelected.add(q.id));
    setSelectedIds(newSelected);
  };

  const deselectAllInGroup = (group) => {
    const newSelected = new Set(selectedIds);
    group.questions.forEach(q => newSelected.delete(q.id));
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    const allIds = new Set();
    stagingData.file_groups.forEach(group => {
      group.questions.forEach(q => allIds.add(q.id));
    });
    setSelectedIds(allIds);
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleApproveSelected = async () => {
    if (selectedIds.size === 0) {
      toast.error('No questions selected');
      return;
    }

    if (!window.confirm(`Approve ${selectedIds.size} questions?`)) return;

    try {
      const response = await axios.post(`${API}/admin/bulk-import/approve`, {
        staging_ids: Array.from(selectedIds)
      });
      
      const { approved_count, errors } = response.data;
      
      if (approved_count > 0) {
        toast.success(`Approved ${approved_count} questions!`);
      }
      
      // Show errors if any questions failed to approve
      if (errors && errors.length > 0) {
        console.error('Approval errors:', errors);
        toast.error(`${errors.length} question(s) failed to approve. Check console for details.`);
      }
      
      setSelectedIds(new Set());
      setSelectedQuestion(null);
      fetchStagingData();
    } catch (error) {
      console.error('Failed to approve:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve questions');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) {
      toast.error('No questions selected');
      return;
    }

    if (!window.confirm(`Delete ${selectedIds.size} staging questions? This cannot be undone.`)) return;

    try {
      // Use bulk delete endpoint - single API call, single DB operation
      const response = await axios.post(`${API}/admin/bulk-import/staging/bulk-delete`, {
        staging_ids: Array.from(selectedIds)
      });
      
      const { deleted_count, requested_count } = response.data;
      
      if (deleted_count === requested_count) {
        toast.success(`Deleted ${deleted_count} questions`);
      } else {
        toast.warning(`Deleted ${deleted_count} of ${requested_count} questions`);
      }
      
      setSelectedIds(new Set());
      setSelectedQuestion(null);
      fetchStagingData();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete questions');
      fetchStagingData();
    }
  };

  // Open merge dialog with a target question
  const openMergeDialog = (targetQuestion) => {
    setMergeTargetQuestion(targetQuestion);
    setMergeOptions({
      updateAnswer: false
    });
    setMergeDialogOpen(true);
  };

  // Handle merge with existing question
  const handleMergeWithExisting = async () => {
    if (!selectedQuestion || !mergeTargetQuestion) return;
    
    setIsMerging(true);
    try {
      await axios.post(`${API}/admin/questions/merge`, {
        source_question_id: selectedQuestion.id,
        target_question_id: mergeTargetQuestion.id,
        update_answer: mergeOptions.updateAnswer,
        merge_pictures: false,  // Bulk import doesn't have pictures, so always false
        source_type: 'staging'  // Important: this is from bulk import staging
      });
      
      toast.success('Question merged successfully! Surveyor added to question history.');
      
      setMergeDialogOpen(false);
      setMergeTargetQuestion(null);
      setSelectedQuestion(null);
      setSimilarityData(null);
      fetchStagingData();
    } catch (error) {
      console.error('Failed to merge questions:', error);
      toast.error(error.response?.data?.detail || 'Failed to merge questions');
    } finally {
      setIsMerging(false);
    }
  };

  const handleMarkStagingDuplicate = async (originalStagingId) => {
    if (!selectedQuestion) return;

    try {
      const response = await axios.post(
        `${API}/admin/bulk-import/mark-staging-duplicate?staging_id=${selectedQuestion.id}&original_staging_id=${originalStagingId}`
      );
      
      toast.success(response.data.message);
      setSelectedQuestion(null);
      setSimilarityData(null);
      fetchStagingData();
    } catch (error) {
      console.error('Failed to mark as staging duplicate:', error);
      toast.error('Failed to mark as duplicate');
    }
  };

  const handleOpenEdit = () => {
    if (!selectedQuestion) return;
    setEditData({
      question_text: selectedQuestion.question_text,
      asked_by_internal: selectedQuestion.asked_by_internal,
      surveyor_internal_id: selectedQuestion.surveyor_internal_id || '',
      surveyor_external_id: selectedQuestion.surveyor_external_id || '',
      exam_month: selectedQuestion.exam_month || '',
      asked: selectedQuestion.asked || 1,
      is_decider: selectedQuestion.is_decider || false
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedQuestion) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/admin/bulk-import/staging/${selectedQuestion.id}`, editData);
      toast.success('Question updated');
      setEditDialogOpen(false);
      fetchStagingData();
      
      // Update selected question
      setSelectedQuestion({ ...selectedQuestion, ...editData });
    } catch (error) {
      console.error('Failed to update:', error);
      toast.error('Failed to update question');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenBulkEdit = async () => {
    if (selectedIds.size === 0) {
      toast.error('No questions selected');
      return;
    }
    // Reset bulk edit form
    setBulkEditData({
      updateSurveyor: false,
      asked_by_internal: false,
      surveyor_id: '',
      updateMonthYear: false,
      updateDecider: false,
      exam_month: months[new Date().getMonth()],
      exam_year: currentYear.toString(),
      is_decider: false
    });
    
    // Load surveyors for bulk edit based on first selected question's grade/mmd
    await loadSurveyorsForBulkEdit();
    setBulkEditDialogOpen(true);
  };

  const loadSurveyorsForBulkEdit = async () => {
    // Find first selected question to get grade/mmd
    let firstSelectedQuestion = null;
    for (const group of stagingData.file_groups) {
      for (const q of group.questions) {
        if (selectedIds.has(q.id)) {
          firstSelectedQuestion = q;
          break;
        }
      }
      if (firstSelectedQuestion) break;
    }
    
    if (firstSelectedQuestion) {
      await fetchSurveyorsForQuestion(firstSelectedQuestion.grade, firstSelectedQuestion.mmd_location);
    }
  };

  const handleSaveBulkEdit = async () => {
    setBulkSaving(true);
    try {
      const updatePayload = {
        staging_ids: Array.from(selectedIds)
      };

      // Surveyor update (includes asked_by_internal logic)
      if (bulkEditData.updateSurveyor) {
        updatePayload.asked_by_internal = bulkEditData.asked_by_internal;
        
        if (bulkEditData.surveyor_id) {
          if (bulkEditData.asked_by_internal) {
            updatePayload.surveyor_internal_id = bulkEditData.surveyor_id;
            updatePayload.surveyor_external_id = null; // Clear external
          } else {
            updatePayload.surveyor_external_id = bulkEditData.surveyor_id;
            updatePayload.surveyor_internal_id = null; // Clear internal
          }
        }
      }
      
      if (bulkEditData.updateMonthYear) {
        updatePayload.exam_month = `${bulkEditData.exam_month} ${bulkEditData.exam_year}`;
      }
      
      if (bulkEditData.updateDecider) {
        updatePayload.is_decider = bulkEditData.is_decider;
      }

      const response = await axios.post(`${API}/admin/bulk-import/staging/bulk-edit`, updatePayload);
      toast.success(response.data.message);
      setBulkEditDialogOpen(false);
      setSelectedIds(new Set());
      fetchStagingData();
    } catch (error) {
      console.error('Failed to bulk edit:', error);
      toast.error('Failed to bulk edit questions');
    } finally {
      setBulkSaving(false);
    }
  };

  const getSurveyorName = (question) => {
    const surveyorId = question.asked_by_internal 
      ? question.surveyor_internal_id 
      : question.surveyor_external_id;
    const surveyor = surveyors.find(s => s.id === surveyorId);
    return surveyor?.name || 'Not assigned';
  };

  const internalSurveyors = surveyors.filter(s => s.type === 'internal');
  const externalSurveyors = surveyors.filter(s => s.type === 'external');
  
  // Group surveyors by MMD for Nautical grades
  const groupSurveyorsByMMD = (surveyorList) => {
    const grouped = {};
    surveyorList.forEach(s => {
      const homeMmd = s.mmd_locations?.[0] || 'Unknown';
      if (!grouped[homeMmd]) grouped[homeMmd] = [];
      grouped[homeMmd].push(s);
    });
    return grouped;
  };
  
  // Check if current question is Nautical grade
  const isCurrentQuestionNautical = selectedQuestion ? isNauticalGrade(selectedQuestion.grade) : false;
  const internalGroupedByMMD = isCurrentQuestionNautical ? groupSurveyorsByMMD(internalSurveyors) : null;
  const externalGroupedByMMD = isCurrentQuestionNautical ? groupSurveyorsByMMD(externalSurveyors) : null;

  return (
    <Layout>
      <div className="h-[calc(100vh-64px)] flex flex-col">
        {/* Header */}
        <div className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/bulk-import')}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-1">
                  Bulk Import Review
                  <StagingActionsHelpPopover testId="page-staging-help-btn" align="start" />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-gray-500 hover:text-red-600"
                    onClick={() => setTrashOpen(true)}
                    data-testid="open-staging-trash-btn"
                    title="View & restore soft-deleted staging rows (7-day retention)"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Trash
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-gray-500 hover:text-indigo-600"
                    onClick={() => navigate('/admin/bulk-import-audit')}
                    data-testid="open-audit-log-btn"
                    title="Bulk-Import forensic audit — every action on every batch (90-day retention)"
                  >
                    <ClipboardList className="w-3.5 h-3.5 mr-1" /> Audit Log
                  </Button>
                </h1>
                <p className="text-sm text-gray-500">
                  {stagingData.total_count} questions in staging
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={selectAll}>
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={deselectAll}>
                Deselect All
              </Button>
              <Button 
                variant="secondary" 
                size="sm"
                onClick={handleOpenBulkEdit}
                disabled={selectedIds.size === 0}
              >
                <Edit2 className="h-4 w-4 mr-1" />
                Bulk Edit ({selectedIds.size})
              </Button>
              <Button 
                variant="destructive" 
                size="sm"
                onClick={handleDeleteSelected}
                disabled={selectedIds.size === 0}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete ({selectedIds.size})
              </Button>
              <Button 
                size="sm"
                onClick={handleApproveSelected}
                disabled={selectedIds.size === 0}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Approve ({selectedIds.size})
              </Button>
            </div>
          </div>
          
          {/* Filters */}
          <div className="flex gap-4 mt-4">
            <Select value={filterGrade || "all"} onValueChange={(v) => setFilterGrade(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Grades" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Grades</SelectItem>
                {grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
            
            <Select value={filterMMD || "all"} onValueChange={(v) => setFilterMMD(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All MMDs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All MMDs</SelectItem>
                {mmdLocations.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            
            {/* Searchable Function Filter */}
            <Popover open={functionComboOpen} onOpenChange={setFunctionComboOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={functionComboOpen}
                  className="w-56 justify-between"
                >
                  {filterFunction
                    ? (() => {
                        const f = allFunctions.find(fn => fn.id === filterFunction);
                        if (!f) return 'All Functions';
                        const suffix = f.function_suffix && f.function_suffix !== 'None' && f.function_suffix !== 'null' ? f.function_suffix : '';
                        return `F${f.function_number}${suffix} - ${f.name}`;
                      })()
                    : 'All Functions'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search functions..." />
                  <CommandList>
                    <CommandEmpty>No functions found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="all"
                        onSelect={() => {
                          setFilterFunction('');
                          setFunctionComboOpen(false);
                        }}
                      >
                        <Check className={cn("mr-2 h-4 w-4", !filterFunction ? "opacity-100" : "opacity-0")} />
                        All Functions
                      </CommandItem>
                      {filteredFunctions.map(f => {
                        const suffix = f.function_suffix && f.function_suffix !== 'None' && f.function_suffix !== 'null' ? f.function_suffix : '';
                        const displayName = `F${f.function_number}${suffix} - ${f.name}`;
                        return (
                          <CommandItem
                            key={f.id}
                            value={displayName}
                            onSelect={() => {
                              setFilterFunction(f.id);
                              setFunctionComboOpen(false);
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", filterFunction === f.id ? "opacity-100" : "opacity-0")} />
                            {displayName}
                            <span className="ml-auto text-xs text-gray-400">{f.grade}</span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Questions List */}
          <div className="w-1/2 border-r overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : stagingData.file_groups.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <FileSpreadsheet className="h-12 w-12 mb-4 text-gray-300" />
                <p>No staging questions found</p>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => navigate('/admin/bulk-import')}
                >
                  Upload questions →
                </Button>
              </div>
            ) : (
              <div className="p-4 space-y-4">
                {stagingData.file_groups.map((group) => (
                  <div key={group.import_batch_id} className="border rounded-lg overflow-hidden">
                    {/* Group Header */}
                    <div 
                      className="bg-gray-50 px-4 py-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleGroup(group.import_batch_id)}
                    >
                      <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
                        {expandedGroups.has(group.import_batch_id) ? (
                          <ChevronDown className="h-4 w-4 shrink-0" />
                        ) : (
                          <ChevronRight className="h-4 w-4 shrink-0" />
                        )}
                        <FileSpreadsheet className="h-4 w-4 text-green-600 shrink-0" />
                        <MiddleTruncate
                          value={group.filename}
                          tailChars={12}
                          className="font-medium max-w-[180px] sm:max-w-[280px] lg:max-w-[420px] xl:max-w-[560px]"
                        />
                        <Badge variant="secondary" className="shrink-0">{group.questions.length} questions</Badge>
                        {/* Show filter tags to distinguish uploads with same filename */}
                        {group.grade && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 shrink-0">{group.grade}</Badge>
                        )}
                        {group.mmd_location && (
                          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 shrink-0">{group.mmd_location}</Badge>
                        )}
                        {group.function_name && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 shrink-0">{group.function_name}</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Link
                          to={`/admin/bulk-import-review/batch/${group.import_batch_id}`}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`open-bulk-sim-${group.import_batch_id}`}
                        >
                          <Button
                            variant="default"
                            size="sm"
                            className="bg-amber-500 hover:bg-amber-600 text-white h-8 w-8 p-0"
                            title="Open Bulk Similarity triage"
                            aria-label="Open Bulk Similarity triage"
                          >
                            <Zap className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); selectAllInGroup(group); }}
                        >
                          Select All
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); deselectAllInGroup(group); }}
                        >
                          Deselect
                        </Button>
                      </div>
                    </div>
                    
                    {/* Questions */}
                    {expandedGroups.has(group.import_batch_id) && (
                      <div className="divide-y">
                        {group.questions.map((question, idx) => (
                          <div 
                            key={question.id}
                            className={`px-4 py-3 flex items-start gap-3 cursor-pointer hover:bg-blue-50 transition-colors ${
                              selectedQuestion?.id === question.id ? 'bg-blue-100' : ''
                            }`}
                            onClick={() => handleSelectQuestion(question)}
                          >
                            <Checkbox
                              checked={selectedIds.has(question.id)}
                              onCheckedChange={() => toggleSelectQuestion(question.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs text-gray-400">Q{question.row_number - 1}</span>
                                {question.is_decider && (
                                  <Badge variant="outline" className="text-xs">Decider</Badge>
                                )}
                                {question.asked > 1 && (
                                  <Badge className="bg-orange-500 text-xs">Asked {question.asked}x</Badge>
                                )}
                                {hasAskedByHistory(question) && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setHistoryModal({ open: true, question });
                                    }}
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium border border-blue-300 bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors"
                                    title="View surveyor history"
                                  >
                                    <History className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                              <p className="text-sm text-gray-800 line-clamp-2">
                                {question.question_text}
                              </p>
                              <div className="flex gap-2 mt-1">
                                <span className="text-xs text-gray-500">
                                  {question.asked_by_internal ? 'Internal' : 'External'}
                                </span>
                                <span className="text-xs text-gray-500">•</span>
                                <span className="text-xs text-gray-500">{question.exam_month || 'No date'}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Details Panel */}
          <div className="w-1/2 overflow-auto bg-gray-50">
            {selectedQuestion ? (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Question Details</h2>
                  <Button variant="outline" size="sm" onClick={handleOpenEdit}>
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                </div>

                <Card className="mb-4">
                  <CardContent className="pt-4">
                    <p className="text-gray-800 mb-4">{selectedQuestion.question_text}</p>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <Label className="text-gray-500">Grade</Label>
                        <p>{selectedQuestion.grade}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">MMD</Label>
                        <p>{selectedQuestion.mmd_location}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Surveyor Type</Label>
                        <p>{selectedQuestion.asked_by_internal ? 'Internal' : 'External'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Surveyor</Label>
                        <p>{getSurveyorName(selectedQuestion)}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Month/Year</Label>
                        <p>{selectedQuestion.exam_month || 'Not set'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Asked Count</Label>
                        <p>{selectedQuestion.asked || 1}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Decider</Label>
                        <p>{selectedQuestion.is_decider ? 'Yes' : 'No'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Source File</Label>
                        <p className="text-xs truncate">{selectedQuestion.source_filename}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Similarity Section */}
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Similar Questions
                </h3>

                {loadingSimilarity ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : similarityData ? (
                  <div className="space-y-4">
                    {/* Approved Similar */}
                    {similarityData.approved_similar.length > 0 && (
                      <Card>
                        <CardHeader className="py-3">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            From Approved Questions
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          {similarityData.approved_similar.map((similar) => (
                            <div key={similar.id} className="border-t py-3 first:border-t-0 first:pt-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm text-gray-700 flex-1">{similar.question_text}</p>
                                <Badge 
                                  variant="outline" 
                                  className={cn("shrink-0", 
                                    Number(similar.similarity) >= 80 ? "border-red-400 text-red-700 bg-red-50" :
                                    Number(similar.similarity) >= 65 ? "border-amber-400 text-amber-700 bg-amber-50" :
                                    "border-blue-400 text-blue-700 bg-blue-50"
                                  )}
                                >
                                  {Number(similar.similarity).toFixed(1)}% semantic
                                </Badge>
                              </div>
                              {/* Info tags */}
                              <div className="flex flex-wrap gap-1 mt-2">
                                <Badge variant="secondary" className="text-xs">{similar.grade}</Badge>
                                <Badge variant="secondary" className="text-xs">{similar.mmd_location}</Badge>
                                <Badge variant="secondary" className="text-xs">{similar.function_name}</Badge>
                                <Badge variant="secondary" className="text-xs">{similar.surveyor_name}</Badge>
                              </div>
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-xs text-gray-500">
                                  Asked: {similar.asked} | {similar.exam_month || 'No date'}
                                </span>
                                <div className="flex gap-2">
                                  <Button 
                                    size="sm"
                                    onClick={() => openMergeDialog(similar)}
                                    className="bg-[#0A7EA4] hover:bg-[#086b8a] text-white"
                                  >
                                    Merge with Existing
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}

                    {/* Staging Similar */}
                    {similarityData.staging_similar.length > 0 && (
                      <Card>
                        <CardHeader className="py-3">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-amber-600" />
                            From This Staging Batch
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          {similarityData.staging_similar.map((similar) => (
                            <div key={similar.id} className="border-t py-3 first:border-t-0 first:pt-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm text-gray-700 flex-1">{similar.question_text}</p>
                                <Badge 
                                  variant="outline" 
                                  className={cn("shrink-0", 
                                    Number(similar.similarity) >= 80 ? "border-red-400 text-red-700 bg-red-50" :
                                    Number(similar.similarity) >= 65 ? "border-amber-400 text-amber-700 bg-amber-50" :
                                    "border-blue-400 text-blue-700 bg-blue-50"
                                  )}
                                >
                                  {Number(similar.similarity).toFixed(1)}% semantic
                                </Badge>
                              </div>
                              {/* Info tags */}
                              <div className="flex flex-wrap gap-1 mt-2">
                                <Badge variant="secondary" className="text-xs">{similar.grade}</Badge>
                                <Badge variant="secondary" className="text-xs">{similar.mmd_location}</Badge>
                                <Badge variant="secondary" className="text-xs">{similar.function_name}</Badge>
                                <Badge variant="secondary" className="text-xs">{similar.surveyor_name}</Badge>
                                <Badge variant="secondary" className="text-xs">{similar.exam_month || 'No date'}</Badge>
                              </div>
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-xs text-gray-500">
                                  From: {similar.source_filename} (Row {similar.row_number}) • Q#{similar.question_number} in staging
                                </span>
                                <Button 
                                  variant="destructive" 
                                  size="sm"
                                  onClick={() => handleMarkStagingDuplicate(similar.id)}
                                >
                                  <Copy className="h-3 w-3 mr-1" />
                                  Merge into this (+{selectedQuestion.asked})
                                </Button>
                              </div>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}

                    {similarityData.approved_similar.length === 0 && similarityData.staging_similar.length === 0 && (
                      <Alert>
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <AlertDescription>
                          No similar questions found. This appears to be unique!
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <Search className="h-12 w-12 mb-4 text-gray-300" />
                <p>Select a question to view details</p>
                <p className="text-sm">and check for duplicates</p>
              </div>
            )}
          </div>
        </div>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Staging Question</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label>Question Text</Label>
                <Input
                  value={editData.question_text || ''}
                  onChange={(e) => setEditData({ ...editData, question_text: e.target.value })}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={editData.asked_by_internal}
                  onCheckedChange={(checked) => setEditData({ 
                    ...editData, 
                    asked_by_internal: checked,
                    surveyor_internal_id: checked ? editData.surveyor_internal_id : '',
                    surveyor_external_id: checked ? '' : editData.surveyor_external_id
                  })}
                />
                <Label>Asked by Internal Surveyor</Label>
              </div>
              
              {editData.asked_by_internal ? (
                <div>
                  <Label>Internal Surveyor</Label>
                  <Popover open={internalSurveyorComboOpen} onOpenChange={setInternalSurveyorComboOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={internalSurveyorComboOpen}
                        className="w-full justify-between mt-1"
                      >
                        {editData.surveyor_internal_id 
                          ? surveyors.find(s => s.id === editData.surveyor_internal_id)?.name || 'Select surveyor...'
                          : 'Select surveyor...'}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[280px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search internal surveyors..." />
                        <CommandList>
                          <CommandEmpty>No surveyors found.</CommandEmpty>
                          {isCurrentQuestionNautical && internalGroupedByMMD ? (
                            // Grouped by MMD for Nautical grades
                            Object.entries(internalGroupedByMMD).map(([mmd, mmdSurveyors]) => (
                              <CommandGroup key={mmd} heading={mmd}>
                                {mmdSurveyors.map(s => (
                                  <CommandItem
                                    key={s.id}
                                    value={`${s.name} ${mmd}`}
                                    onSelect={() => {
                                      setEditData({ ...editData, surveyor_internal_id: s.id });
                                      setInternalSurveyorComboOpen(false);
                                    }}
                                  >
                                    <Check className={cn("mr-2 h-4 w-4", editData.surveyor_internal_id === s.id ? "opacity-100" : "opacity-0")} />
                                    {s.name}{s.is_active === false ? ' (Inactive)' : ''}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            ))
                          ) : (
                            // Flat list for Engineering grades
                            <CommandGroup>
                              {internalSurveyors.map(s => (
                                <CommandItem
                                  key={s.id}
                                  value={s.name}
                                  onSelect={() => {
                                    setEditData({ ...editData, surveyor_internal_id: s.id });
                                    setInternalSurveyorComboOpen(false);
                                  }}
                                >
                                  <Check className={cn("mr-2 h-4 w-4", editData.surveyor_internal_id === s.id ? "opacity-100" : "opacity-0")} />
                                  {s.name}{s.is_active === false ? ' (Inactive)' : ''}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-gray-500 mt-1">
                    {isCurrentQuestionNautical
                      ? `Showing all ${selectedQuestion?.grade} surveyors (any MMD)`
                      : `Filtered by ${selectedQuestion?.mmd_location}`}
                  </p>
                </div>
              ) : (
                <div>
                  <Label>External Surveyor</Label>
                  <Popover open={externalSurveyorComboOpen} onOpenChange={setExternalSurveyorComboOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={externalSurveyorComboOpen}
                        className="w-full justify-between mt-1"
                      >
                        {editData.surveyor_external_id 
                          ? surveyors.find(s => s.id === editData.surveyor_external_id)?.name || 'Select surveyor...'
                          : 'Select surveyor...'}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[280px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search external surveyors..." />
                        <CommandList>
                          <CommandEmpty>No surveyors found.</CommandEmpty>
                          {isCurrentQuestionNautical && externalGroupedByMMD ? (
                            // Grouped by MMD for Nautical grades
                            Object.entries(externalGroupedByMMD).map(([mmd, mmdSurveyors]) => (
                              <CommandGroup key={mmd} heading={mmd}>
                                {mmdSurveyors.map(s => (
                                  <CommandItem
                                    key={s.id}
                                    value={`${s.name} ${mmd}`}
                                    onSelect={() => {
                                      setEditData({ ...editData, surveyor_external_id: s.id });
                                      setExternalSurveyorComboOpen(false);
                                    }}
                                  >
                                    <Check className={cn("mr-2 h-4 w-4", editData.surveyor_external_id === s.id ? "opacity-100" : "opacity-0")} />
                                    {s.name}{s.is_active === false ? ' (Inactive)' : ''}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            ))
                          ) : (
                            // Flat list for Engineering grades
                            <CommandGroup>
                              {externalSurveyors.map(s => (
                                <CommandItem
                                  key={s.id}
                                  value={s.name}
                                  onSelect={() => {
                                    setEditData({ ...editData, surveyor_external_id: s.id });
                                    setExternalSurveyorComboOpen(false);
                                  }}
                                >
                                  <Check className={cn("mr-2 h-4 w-4", editData.surveyor_external_id === s.id ? "opacity-100" : "opacity-0")} />
                                  {s.name}{s.is_active === false ? ' (Inactive)' : ''}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-gray-500 mt-1">
                    {isCurrentQuestionNautical
                      ? `Showing all ${selectedQuestion?.grade} surveyors (any MMD)`
                      : `Filtered by ${selectedQuestion?.mmd_location}`}
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Month</Label>
                  <Select 
                    value={editData.exam_month?.split(' ')[0] || ''} 
                    onValueChange={(v) => {
                      const currentYear = editData.exam_month?.split(' ')[1] || new Date().getFullYear().toString();
                      setEditData({ ...editData, exam_month: `${v} ${currentYear}` });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select month" />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Year</Label>
                  <Select 
                    value={editData.exam_month?.split(' ')[1] || ''} 
                    onValueChange={(v) => {
                      const currentMonth = editData.exam_month?.split(' ')[0] || months[new Date().getMonth()];
                      setEditData({ ...editData, exam_month: `${currentMonth} ${v}` });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => (
                        <SelectItem key={y} value={y}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Asked Count</Label>
                <Input
                  type="number"
                  min="1"
                  value={editData.asked || 1}
                  onChange={(e) => setEditData({ ...editData, asked: parseInt(e.target.value) || 1 })}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={editData.is_decider}
                  onCheckedChange={(checked) => setEditData({ ...editData, is_decider: checked })}
                />
                <Label>Decider Question</Label>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Edit Dialog */}
        <Dialog open={bulkEditDialogOpen} onOpenChange={setBulkEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Bulk Edit {selectedIds.size} Questions</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Select which fields you want to update. Only checked fields will be changed.
              </p>
              
              {/* Surveyor (combined with Asked by Internal) */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Checkbox
                    checked={bulkEditData.updateSurveyor}
                    onCheckedChange={(checked) => setBulkEditData({ 
                      ...bulkEditData, 
                      updateSurveyor: checked,
                      surveyor_id: '' // Reset surveyor when toggling
                    })}
                  />
                  <Label className="font-medium">Update Surveyor</Label>
                </div>
                {bulkEditData.updateSurveyor && (
                  <div className="space-y-3 ml-6">
                    {/* Asked by Internal toggle */}
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={bulkEditData.asked_by_internal}
                        onCheckedChange={(checked) => setBulkEditData({ 
                          ...bulkEditData, 
                          asked_by_internal: checked,
                          surveyor_id: '' // Reset surveyor when changing type
                        })}
                      />
                      <Label>Asked by Internal Surveyor</Label>
                    </div>
                    
                    {/* Surveyor dropdown - shows internal or external based on checkbox - Searchable */}
                    <Popover open={bulkSurveyorComboOpen} onOpenChange={setBulkSurveyorComboOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={bulkSurveyorComboOpen}
                          className="w-full justify-between"
                        >
                          {bulkEditData.surveyor_id 
                            ? surveyors.find(s => s.id === bulkEditData.surveyor_id)?.name || `Select ${bulkEditData.asked_by_internal ? 'internal' : 'external'} surveyor`
                            : `Select ${bulkEditData.asked_by_internal ? 'internal' : 'external'} surveyor`}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[280px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder={`Search ${bulkEditData.asked_by_internal ? 'internal' : 'external'} surveyors...`} />
                          <CommandList>
                            <CommandEmpty>No surveyors found.</CommandEmpty>
                            {bulkEditData.asked_by_internal ? (
                              // Show internal surveyors
                              isCurrentQuestionNautical && internalGroupedByMMD ? (
                                // Grouped by MMD for Nautical
                                Object.entries(internalGroupedByMMD).map(([mmd, mmdSurveyors]) => (
                                  <CommandGroup key={mmd} heading={mmd}>
                                    {mmdSurveyors.map(s => (
                                      <CommandItem
                                        key={s.id}
                                        value={`${s.name} ${mmd}`}
                                        onSelect={() => {
                                          setBulkEditData({ ...bulkEditData, surveyor_id: s.id });
                                          setBulkSurveyorComboOpen(false);
                                        }}
                                      >
                                        <Check className={cn("mr-2 h-4 w-4", bulkEditData.surveyor_id === s.id ? "opacity-100" : "opacity-0")} />
                                        {s.name}{s.is_active === false ? ' (Inactive)' : ''}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                ))
                              ) : (
                                // Flat list for Engineering
                                <CommandGroup>
                                  {internalSurveyors.map(s => (
                                    <CommandItem
                                      key={s.id}
                                      value={s.name}
                                      onSelect={() => {
                                        setBulkEditData({ ...bulkEditData, surveyor_id: s.id });
                                        setBulkSurveyorComboOpen(false);
                                      }}
                                    >
                                      <Check className={cn("mr-2 h-4 w-4", bulkEditData.surveyor_id === s.id ? "opacity-100" : "opacity-0")} />
                                      {s.name}{s.is_active === false ? ' (Inactive)' : ''}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              )
                            ) : (
                              // Show external surveyors
                              isCurrentQuestionNautical && externalGroupedByMMD ? (
                                // Grouped by MMD for Nautical
                                Object.entries(externalGroupedByMMD).map(([mmd, mmdSurveyors]) => (
                                  <CommandGroup key={mmd} heading={mmd}>
                                    {mmdSurveyors.map(s => (
                                      <CommandItem
                                        key={s.id}
                                        value={`${s.name} ${mmd}`}
                                        onSelect={() => {
                                          setBulkEditData({ ...bulkEditData, surveyor_id: s.id });
                                          setBulkSurveyorComboOpen(false);
                                        }}
                                      >
                                        <Check className={cn("mr-2 h-4 w-4", bulkEditData.surveyor_id === s.id ? "opacity-100" : "opacity-0")} />
                                        {s.name}{s.is_active === false ? ' (Inactive)' : ''}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                ))
                              ) : (
                                // Flat list for Engineering
                                <CommandGroup>
                                  {externalSurveyors.map(s => (
                                    <CommandItem
                                      key={s.id}
                                      value={s.name}
                                      onSelect={() => {
                                        setBulkEditData({ ...bulkEditData, surveyor_id: s.id });
                                        setBulkSurveyorComboOpen(false);
                                      }}
                                    >
                                      <Check className={cn("mr-2 h-4 w-4", bulkEditData.surveyor_id === s.id ? "opacity-100" : "opacity-0")} />
                                      {s.name}{s.is_active === false ? ' (Inactive)' : ''}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              )
                            )}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-gray-500 mt-1">
                      {isCurrentQuestionNautical
                        ? `Showing all ${selectedQuestion?.grade} surveyors (any MMD)`
                        : `Filtered by ${selectedQuestion?.mmd_location || 'current question MMD'}`}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Month/Year */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Checkbox
                    checked={bulkEditData.updateMonthYear}
                    onCheckedChange={(checked) => setBulkEditData({ ...bulkEditData, updateMonthYear: checked })}
                  />
                  <Label className="font-medium">Update Month/Year</Label>
                </div>
                {bulkEditData.updateMonthYear && (
                  <div className="grid grid-cols-2 gap-4 ml-6">
                    <Select 
                      value={bulkEditData.exam_month} 
                      onValueChange={(v) => setBulkEditData({ ...bulkEditData, exam_month: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Month" />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map(m => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select 
                      value={bulkEditData.exam_year} 
                      onValueChange={(v) => setBulkEditData({ ...bulkEditData, exam_year: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Year" />
                      </SelectTrigger>
                      <SelectContent>
                        {years.map(y => (
                          <SelectItem key={y} value={y}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              
              {/* Decider */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Checkbox
                    checked={bulkEditData.updateDecider}
                    onCheckedChange={(checked) => setBulkEditData({ ...bulkEditData, updateDecider: checked })}
                  />
                  <Label className="font-medium">Update Decider Status</Label>
                </div>
                {bulkEditData.updateDecider && (
                  <div className="flex items-center gap-2 ml-6">
                    <Checkbox
                      checked={bulkEditData.is_decider}
                      onCheckedChange={(checked) => setBulkEditData({ ...bulkEditData, is_decider: checked })}
                    />
                    <Label>Mark as Decider Question</Label>
                  </div>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveBulkEdit} 
                disabled={bulkSaving || (!bulkEditData.updateSurveyor && !bulkEditData.updateMonthYear && !bulkEditData.updateDecider)}
              >
                {bulkSaving ? 'Updating...' : `Update ${selectedIds.size} Questions`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Merge Question Dialog */}
        <Dialog open={mergeDialogOpen} onOpenChange={(open) => {
          if (!open) {
            setMergeDialogOpen(false);
            setMergeTargetQuestion(null);
          }
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Merge with Existing Question</DialogTitle>
              <DialogDescription>
                Add the new surveyor to the existing question's history. The staging question will be removed.
              </DialogDescription>
            </DialogHeader>
            
            {selectedQuestion && mergeTargetQuestion && (
              <div className="space-y-4">
                {/* Comparison View */}
                <div className="grid grid-cols-2 gap-4">
                  {/* New Submission (Staging) */}
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="text-sm font-semibold text-blue-800 mb-2">New from Import</h4>
                    <p className="text-xs text-gray-600 mb-1">
                      <strong>Surveyor:</strong> {getSurveyorName(selectedQuestion)}
                      {selectedQuestion.asked_by_internal ? ' (Internal)' : ' (External)'}
                    </p>
                    <p className="text-xs text-gray-600 mb-2">
                      <strong>Exam:</strong> {selectedQuestion.exam_month || 'Not specified'}
                    </p>
                    <p className="text-sm text-gray-700">{selectedQuestion.question_text}</p>
                    {selectedQuestion.answer_text && (
                      <div className="mt-2 p-2 bg-white rounded text-xs">
                        <strong>Answer:</strong> {selectedQuestion.answer_text.substring(0, 200)}
                        {selectedQuestion.answer_text.length > 200 ? '...' : ''}
                      </div>
                    )}
                  </div>

                  {/* Existing Question */}
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="text-sm font-semibold text-green-800 mb-2">Existing Question</h4>
                    <p className="text-xs text-gray-600 mb-1">
                      <strong>Surveyor:</strong> {mergeTargetQuestion.surveyor_name || 'Unknown'}
                    </p>
                    <p className="text-xs text-gray-600 mb-1">
                      <strong>Asked:</strong> {mergeTargetQuestion.asked || 1}x
                    </p>
                    <p className="text-xs text-gray-600 mb-2">
                      <strong>Exam:</strong> {mergeTargetQuestion.exam_month || 'Not specified'}
                    </p>
                    <p className="text-sm text-gray-700">{mergeTargetQuestion.question_text}</p>
                  </div>
                </div>

                {/* Merge Options */}
                <div className="p-3 bg-gray-50 rounded-lg space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700">Merge Options</h4>
                  
                  {/* Only show update answer option if the source submission has an answer */}
                  {selectedQuestion?.answer_text && selectedQuestion.answer_text.trim() !== '' && selectedQuestion.answer_text !== 'Coming soon' ? (
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="updateAnswerBulk"
                        checked={mergeOptions.updateAnswer}
                        onCheckedChange={(checked) => setMergeOptions(prev => ({ ...prev, updateAnswer: checked }))}
                      />
                      <div>
                        <label htmlFor="updateAnswerBulk" className="text-sm font-medium cursor-pointer">
                          Update answer with new submission
                        </label>
                        <p className="text-xs text-gray-500">Replace the existing answer with the new one</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 italic">No additional merge options available (submission has no answer)</p>
                  )}
                </div>

                {/* What will happen */}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-amber-800">
                      <p className="font-medium mb-1">After merge:</p>
                      <ul className="list-disc list-inside space-y-0.5">
                        <li>New surveyor will be added to question history</li>
                        <li>Total "asked" count will be updated</li>
                        <li>The staging question will be removed</li>
                        <li>Question will appear in filter results for both surveyors</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setMergeDialogOpen(false);
                  setMergeTargetQuestion(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleMergeWithExisting}
                disabled={isMerging}
                className="bg-[#0A7EA4] hover:bg-[#086b8a]"
              >
                {isMerging ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Merging...
                  </>
                ) : (
                  'Confirm Merge'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* History Modal - Shows all surveyors who asked the question */}
        <Dialog open={historyModal.open} onOpenChange={(open) => !open && setHistoryModal({ open: false, question: null })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-blue-600" />
                Question History
              </DialogTitle>
              <DialogDescription>
                All surveyors who asked this question
              </DialogDescription>
            </DialogHeader>
            {historyModal.question && (
              <div className="mt-4 max-h-[60vh] overflow-y-auto pr-2 space-y-3">
                {getGroupedHistory(historyModal.question).map((entry, idx) => (
                  <div 
                    key={idx} 
                    className="p-3 rounded-lg border bg-gray-50 border-gray-200"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900 flex items-center gap-1.5">
                        <User className="w-4 h-4 text-gray-500" />
                        {entry.surveyor_name}
                        {entry.count > 1 && (
                          <Badge className="bg-orange-500 text-white text-xs ml-1">
                            x{entry.count}
                          </Badge>
                        )}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {entry.surveyor_type}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      {entry.exam_month && formatExamMonth(entry.exam_month) && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatExamMonth(entry.exam_month)}
                        </span>
                      )}
                      {entry.mmd_location && (
                        <span className="text-gray-500">
                          {entry.mmd_location}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <BulkSimilarityModal
          open={bulkSimOpen}
          onClose={() => setBulkSimOpen(false)}
          batchId={bulkSimBatchId}
          scope={bulkSimScope}
          onApplied={fetchStagingData}
        />

        <StagingTrashModal
          open={trashOpen}
          onOpenChange={setTrashOpen}
          onRestored={fetchStagingData}
        />
      </div>
    </Layout>
  );
};

export default BulkImportReviewPage;
